import React, { useMemo } from 'react';

const THEME = {
  bg: '#f9efd5',
  card: 'white',
  border: '#e8cc88',
  primary: '#c4451a',
  primaryDark: '#8f2a0e',
  gold: '#c8924a',
  brown: '#5c3a1e',
  textMuted: '#a07048',
};

/* ── Tiny bar chart (pure SVG, no deps) ──────────────────── */
const BarChart = ({ data, color = THEME.primary, height = 80 }) => {
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="flex items-end gap-1 w-full" style={{ height }}>
      {data.map((d, i) => (
        <div key={i} className="flex flex-col items-center flex-1 gap-1">
          <div
            className="w-full rounded-t-md transition-all duration-500"
            style={{
              height: `${Math.max((d.value / max) * (height - 20), d.value > 0 ? 4 : 0)}px`,
              background: color,
              opacity: 0.85,
            }}
          />
          <span className="text-[9px] font-medium truncate w-full text-center" style={{ color: THEME.textMuted }}>
            {d.label}
          </span>
        </div>
      ))}
    </div>
  );
};

/* ── Donut chart (SVG) ───────────────────────────────────── */
const DonutChart = ({ slices, size = 110 }) => {
  const total = slices.reduce((s, x) => s + x.value, 0) || 1;
  let offset = 0;
  const r = 38, cx = size / 2, cy = size / 2;
  const circumference = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f0e4c8" strokeWidth="14" />
      {slices.map((s, i) => {
        const frac = s.value / total;
        const dash = frac * circumference;
        const el = (
          <circle
            key={i}
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke={s.color}
            strokeWidth="14"
            strokeDasharray={`${dash} ${circumference - dash}`}
            strokeDashoffset={-offset * circumference}
            strokeLinecap="butt"
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        );
        offset += frac;
        return el;
      })}
      <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle" fontSize="16" fontWeight="bold" fill={THEME.brown}>
        {total}
      </text>
      <text x={cx} y={cy + 16} textAnchor="middle" dominantBaseline="middle" fontSize="7" fill={THEME.textMuted}>
        total
      </text>
    </svg>
  );
};

/* ── Stat card ───────────────────────────────────────────── */
const StatCard = ({ icon, label, value, sub, color }) => (
  <div className="rounded-2xl p-5 flex items-center gap-4 shadow-sm border" style={{ background: 'white', borderColor: THEME.border }}>
    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0" style={{ background: color + '22' }}>
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-2xl font-black leading-none" style={{ color: THEME.brown }}>{value}</p>
      <p className="text-sm font-semibold mt-0.5 truncate" style={{ color: THEME.brown }}>{label}</p>
      {sub && <p className="text-xs mt-0.5 truncate" style={{ color: THEME.textMuted }}>{sub}</p>}
    </div>
  </div>
);

/* ── Section card wrapper ────────────────────────────────── */
const Section = ({ title, icon, children, className = '' }) => (
  <div className={`rounded-2xl border shadow-sm overflow-hidden ${className}`} style={{ background: 'white', borderColor: THEME.border }}>
    <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: THEME.border, background: '#fdf8ee' }}>
      <span className="text-lg">{icon}</span>
      <h3 className="font-bold text-sm" style={{ color: THEME.brown }}>{title}</h3>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

/* ── Legend row ──────────────────────────────────────────── */
const LegendRow = ({ color, label, value, pct }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} />
    <span className="flex-1 truncate" style={{ color: THEME.brown }}>{label}</span>
    <span className="font-bold tabular-nums" style={{ color: THEME.brown }}>{value}</span>
    {pct !== undefined && <span className="text-[10px]" style={{ color: THEME.textMuted }}>({pct}%)</span>}
  </div>
);

/* ── Progress bar ────────────────────────────────────────── */
const ProgressBar = ({ label, value, max, color }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs mb-1">
        <span className="font-medium truncate" style={{ color: THEME.brown }}>{label}</span>
        <span className="font-bold ml-2 shrink-0" style={{ color: THEME.brown }}>{value} <span style={{ color: THEME.textMuted }}>({pct}%)</span></span>
      </div>
      <div className="h-2 rounded-full" style={{ background: '#f0e4c8' }}>
        <div className="h-2 rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════ */
const AdminAnalytics = ({ donors = [], requests = [], admins = [], donationHistory = [] }) => {
  const now = new Date();

  const analytics = useMemo(() => {
    /* ── Donors ── */
    const totalDonors = donors.length;
    const availableDonors = donors.filter(d => d.is_available !== false).length;
    const unavailableDonors = totalDonors - availableDonors;

    // Blood group distribution
    const bloodGroupMap = {};
    donors.forEach(d => {
      const bg = d.blood_group || 'Unknown';
      bloodGroupMap[bg] = (bloodGroupMap[bg] || 0) + 1;
    });
    const bloodGroupData = Object.entries(bloodGroupMap)
      .sort((a, b) => b[1] - a[1])
      .map(([bg, count]) => ({ label: bg, value: count }));

    // District distribution - detailed
    const districtMap = {};
    donors.forEach(d => {
      const dist = d.district || 'Unknown';
      if (!districtMap[dist]) {
        districtMap[dist] = { total: 0, available: 0, donated: 0 };
      }
      districtMap[dist].total++;
      if (d.is_available !== false) districtMap[dist].available++;
      if ((d.total_donations || 0) > 0) districtMap[dist].donated++;
    });
    
    const allDistricts = Object.entries(districtMap)
      .sort((a, b) => b[1].total - a[1].total)
      .map(([name, data]) => ({ 
        name, 
        total: data.total, 
        available: data.available, 
        donated: data.donated,
        pct: Math.round((data.available / data.total) * 100)
      }));
    
    const districtData = allDistricts.slice(0, 8).map(d => ({ 
      label: d.name.split(' ')[0], 
      value: d.total, 
      full: d.name 
    }));

    // Parish breakdown
    const parishMap = {};
    donors.forEach(d => {
      const parish = d.parish || 'Unknown';
      const dist = d.district || 'Unknown';
      const key = `${parish} (${dist})`;
      if (!parishMap[key]) {
        parishMap[key] = { name: parish, district: dist, count: 0 };
      }
      parishMap[key].count++;
    });
    const topParishes = Object.values(parishMap)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Donors registered per month (last 6 months)
    const donorsByMonth = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      const label = d.toLocaleString('default', { month: 'short' });
      const value = donors.filter(don => {
        const created = new Date(don.created_at);
        return created.getFullYear() === d.getFullYear() && created.getMonth() === d.getMonth();
      }).length;
      return { label, value };
    });

    // Eligibility
    const eligibleNow = donors.filter(d => {
      if (!d.next_eligible_date) return true;
      return new Date(d.next_eligible_date) <= now;
    }).length;

    // Total recorded donations from history
    const totalRecordedDonations = donationHistory.length;
    const totalDonationsFromDonors = donors.reduce((s, d) => s + (d.total_donations || 0), 0);
    
    // Unique donors who have actually donated
    const donorsWhoDonated = new Set(donationHistory.map(h => h.donor_id)).size;
    const donorsWhoNeverDonated = totalDonors - donorsWhoDonated;

    // Top donors by donation history
    const donationCountByDonor = {};
    donationHistory.forEach(h => {
      donationCountByDonor[h.donor_id] = (donationCountByDonor[h.donor_id] || 0) + 1;
    });
    
    const topDonors = [...donors]
      .filter(d => donationCountByDonor[d.id] > 0)
      .sort((a, b) => (donationCountByDonor[b.id] || 0) - (donationCountByDonor[a.id] || 0))
      .slice(0, 5)
      .map(d => ({ ...d, actualDonations: donationCountByDonor[d.id] || 0 }));

    // Recent donations (last 10)
    const recentDonations = [...donationHistory]
      .sort((a, b) => new Date(b.donation_date) - new Date(a.donation_date))
      .slice(0, 10)
      .map(h => {
        const donor = donors.find(d => d.id === h.donor_id);
        return {
          ...h,
          donorName: donor?.name || 'Unknown',
          bloodGroup: donor?.blood_group || '-',
          district: donor?.district || '-'
        };
      });

    // Donations by month (last 6 months)
    const donationsByMonth = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      const label = d.toLocaleString('default', { month: 'short' });
      const value = donationHistory.filter(h => {
        const donationDate = new Date(h.donation_date);
        return donationDate.getFullYear() === d.getFullYear() && donationDate.getMonth() === d.getMonth();
      }).length;
      return { label, value };
    });

    /* ── Requests ── */
    const totalRequests = requests.length;
    const activeReq = requests.filter(r => r.status === 'active').length;
    const fulfilledReq = requests.filter(r => r.status === 'fulfilled').length;
    const expiredReq = requests.filter(r => r.status === 'expired').length;
    const cancelledReq = requests.filter(r => r.status === 'cancelled').length;
    const fulfillmentRate = totalRequests > 0 ? Math.round((fulfilledReq / totalRequests) * 100) : 0;

    // Requests per month (last 6)
    const requestsByMonth = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      const label = d.toLocaleString('default', { month: 'short' });
      const value = requests.filter(r => {
        const created = new Date(r.created_at);
        return created.getFullYear() === d.getFullYear() && created.getMonth() === d.getMonth();
      }).length;
      return { label, value };
    });

    // Fulfilled this month
    const fulfilledThisMonth = requests.filter(r => {
      const d = new Date(r.fulfilled_at || r.updated_at || r.created_at);
      return r.status === 'fulfilled' && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;

    // Urgency breakdown
    const urgencyMap = { critical: 0, high: 0, medium: 0, low: 0 };
    requests.forEach(r => { if (urgencyMap[r.urgency_level] !== undefined) urgencyMap[r.urgency_level]++; });

    // Blood group demand
    const reqBloodGroupMap = {};
    requests.forEach(r => {
      const bg = r.blood_group || 'Unknown';
      reqBloodGroupMap[bg] = (reqBloodGroupMap[bg] || 0) + 1;
    });
    const reqBloodGroupData = Object.entries(reqBloodGroupMap)
      .sort((a, b) => b[1] - a[1])
      .map(([bg, count]) => ({ label: bg, value: count }));

    /* ── Admins ── */
    const totalAdmins = admins.length;
    const roleMap = {};
    admins.forEach(a => {
      const role = a.role || 'Unknown';
      roleMap[role] = (roleMap[role] || 0) + 1;
    });

    return {
      donors: { 
        totalDonors, 
        availableDonors, 
        unavailableDonors, 
        bloodGroupData, 
        districtData, 
        allDistricts,
        topParishes,
        donorsByMonth, 
        eligibleNow, 
        totalRecordedDonations,
        totalDonationsFromDonors,
        donorsWhoDonated,
        donorsWhoNeverDonated,
        topDonors,
        recentDonations,
        donationsByMonth
      },
      requests: { totalRequests, activeReq, fulfilledReq, expiredReq, cancelledReq, fulfillmentRate, requestsByMonth, fulfilledThisMonth, urgencyMap, reqBloodGroupData },
      admins: { totalAdmins, roleMap },
    };
  }, [donors, requests, admins, donationHistory]);

  const { donors: d, requests: r, admins: a } = analytics;

  const BLOOD_COLORS = ['#c4451a','#8f2a0e','#d4a96a','#c8924a','#7B1A10','#e8aa60','#5c3a1e','#a07048'];
  const URGENCY_COLORS = { critical: '#c4451a', high: '#d97706', medium: '#c8924a', low: '#7cb3b0' };

  return (
    <div className="pb-24 md:pb-6 space-y-6">

      {/* ── Page title ── */}
      <div>
        <h2 className="text-2xl font-black" style={{ color: THEME.brown, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>📊 Analytics</h2>
        <p className="text-sm mt-1" style={{ color: THEME.textMuted }}>Live snapshot of donors, requests, and activity</p>
      </div>

      {/* ── Top KPI row ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="👥" label="Total Donors" value={d.totalDonors} sub={`${d.availableDonors} available`} color="#3b82f6" />
        <StatCard icon="🩸" label="Total Requests" value={r.totalRequests} sub={`${r.activeReq} active now`} color="#c4451a" />
        <StatCard icon="✅" label="Fulfilled" value={r.fulfilledReq} sub={`${r.fulfillmentRate}% fulfillment rate`} color="#16a34a" />
        <StatCard icon="💉" label="Donations Recorded" value={d.totalRecordedDonations} sub={`${d.eligibleNow} eligible now`} color="#c8924a" />
      </div>

      {/* ── Donors + Requests trend row ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="New Donor Registrations (6 months)" icon="📈">
          <BarChart data={d.donorsByMonth} color="#3b82f6" height={100} />
        </Section>
        <Section title="Blood Requests Submitted (6 months)" icon="📉">
          <BarChart data={r.requestsByMonth} color={THEME.primary} height={100} />
        </Section>
      </div>

      {/* ── Donor breakdown ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Availability donut */}
        <Section title="Donor Availability" icon="🟢">
          <div className="flex items-center gap-4">
            <DonutChart
              slices={[
                { value: d.availableDonors, color: '#16a34a' },
                { value: d.unavailableDonors, color: '#fca5a5' },
              ]}
            />
            <div className="space-y-2 flex-1">
              <LegendRow color="#16a34a" label="Available" value={d.availableDonors} pct={d.totalDonors > 0 ? Math.round(d.availableDonors / d.totalDonors * 100) : 0} />
              <LegendRow color="#fca5a5" label="Unavailable" value={d.unavailableDonors} pct={d.totalDonors > 0 ? Math.round(d.unavailableDonors / d.totalDonors * 100) : 0} />
              <LegendRow color={THEME.gold} label="Eligible now" value={d.eligibleNow} />
            </div>
          </div>
        </Section>

        {/* Blood group supply */}
        <Section title="Donors by Blood Group" icon="🩸">
          {d.bloodGroupData.length === 0 ? (
            <p className="text-xs text-center py-4" style={{ color: THEME.textMuted }}>No data yet</p>
          ) : (
            <div className="space-y-1.5">
              {d.bloodGroupData.map((bg, i) => (
                <ProgressBar key={bg.label} label={bg.label} value={bg.value} max={d.totalDonors} color={BLOOD_COLORS[i % BLOOD_COLORS.length]} />
              ))}
            </div>
          )}
        </Section>

        {/* District coverage */}
        <Section title="Top Districts (Donors)" icon="📍">
          <BarChart data={d.districtData} color={THEME.gold} height={110} />
        </Section>
      </div>

      {/* ── Full District Breakdown ── */}
      <Section title="District-wise Donor Analysis" icon="🗺️" className="overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 max-h-96 overflow-y-auto">
          {d.allDistricts.map((district) => (
            <div key={district.name} className="rounded-xl p-3 border" style={{ background: '#fdf8ee', borderColor: THEME.border }}>
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-bold text-sm truncate" style={{ color: THEME.brown }}>{district.name}</h4>
                <span className="text-lg font-black" style={{ color: THEME.primary }}>{district.total}</span>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span style={{ color: THEME.textMuted }}>Available</span>
                  <span className="font-semibold" style={{ color: '#16a34a' }}>{district.available} ({district.pct}%)</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: THEME.textMuted }}>Have Donated</span>
                  <span className="font-semibold" style={{ color: THEME.primary }}>{district.donated}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Parish Breakdown ── */}
      <Section title="Top Parishes by Donor Count" icon="⛪">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {d.topParishes.map((parish, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-xl border" style={{ background: 'white', borderColor: THEME.border }}>
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate" style={{ color: THEME.brown }}>{parish.name}</p>
                <p className="text-xs truncate" style={{ color: THEME.textMuted }}>{parish.district}</p>
              </div>
              <span className="text-lg font-bold ml-2" style={{ color: THEME.gold }}>{parish.count}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Request breakdown ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Status donut */}
        <Section title="Request Status Breakdown" icon="📋">
          <div className="flex items-center gap-4">
            <DonutChart
              slices={[
                { value: r.activeReq, color: '#16a34a' },
                { value: r.fulfilledReq, color: '#3b82f6' },
                { value: r.expiredReq, color: '#9ca3af' },
                { value: r.cancelledReq, color: '#ef4444' },
              ]}
            />
            <div className="space-y-2 flex-1">
              <LegendRow color="#16a34a" label="Active" value={r.activeReq} />
              <LegendRow color="#3b82f6" label="Fulfilled" value={r.fulfilledReq} />
              <LegendRow color="#9ca3af" label="Expired" value={r.expiredReq} />
              <LegendRow color="#ef4444" label="Cancelled" value={r.cancelledReq} />
            </div>
          </div>
          <div className="mt-4 pt-3 border-t text-xs flex items-center justify-between" style={{ borderColor: THEME.border }}>
            <span style={{ color: THEME.textMuted }}>Fulfilled this month</span>
            <span className="font-bold" style={{ color: THEME.brown }}>{r.fulfilledThisMonth}</span>
          </div>
        </Section>

        {/* Urgency breakdown */}
        <Section title="Requests by Urgency" icon="⚡">
          <div className="space-y-2">
            {Object.entries(r.urgencyMap).map(([level, count]) => (
              <ProgressBar key={level} label={level.charAt(0).toUpperCase() + level.slice(1)} value={count} max={r.totalRequests || 1} color={URGENCY_COLORS[level]} />
            ))}
          </div>
        </Section>

        {/* Blood group demand */}
        <Section title="Blood Group Demand" icon="🔴">
          {r.reqBloodGroupData.length === 0 ? (
            <p className="text-xs text-center py-4" style={{ color: THEME.textMuted }}>No data yet</p>
          ) : (
            <div className="space-y-1.5">
              {r.reqBloodGroupData.slice(0, 6).map((bg, i) => (
                <ProgressBar key={bg.label} label={bg.label} value={bg.value} max={r.totalRequests || 1} color={BLOOD_COLORS[i % BLOOD_COLORS.length]} />
              ))}
            </div>
          )}
        </Section>
      </div>

      {/* ── Donation Activity Row ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Recent Donations */}
        <Section title="Recent Blood Donations" icon="💉">
          {d.recentDonations.length === 0 ? (
            <p className="text-xs text-center py-4" style={{ color: THEME.textMuted }}>No donations recorded yet</p>
          ) : (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {d.recentDonations.map((donation, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-xl" style={{ background: '#fdf8ee' }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-xs shrink-0"
                    style={{ background: `linear-gradient(135deg, #16a34a, #22c55e)` }}>
                    💉
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: THEME.brown }}>{donation.donorName}</p>
                    <p className="text-xs truncate" style={{ color: THEME.textMuted }}>
                      {donation.bloodGroup} · {donation.district} · {donation.hospital || 'Unknown Hospital'}
                    </p>
                  </div>
                  <span className="text-xs font-medium shrink-0" style={{ color: THEME.textMuted }}>
                    {new Date(donation.donation_date).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Donor Participation Stats */}
        <Section title="Donor Participation" icon="📊">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <DonutChart
                slices={[
                  { value: d.donorsWhoDonated, color: '#16a34a' },
                  { value: d.donorsWhoNeverDonated, color: '#fca5a5' },
                ]}
              />
              <div className="space-y-2 flex-1">
                <LegendRow color="#16a34a" label="Have Donated" value={d.donorsWhoDonated} pct={d.totalDonors > 0 ? Math.round(d.donorsWhoDonated / d.totalDonors * 100) : 0} />
                <LegendRow color="#fca5a5" label="Never Donated" value={d.donorsWhoNeverDonated} pct={d.totalDonors > 0 ? Math.round(d.donorsWhoNeverDonated / d.totalDonors * 100) : 0} />
              </div>
            </div>
            <div className="pt-3 border-t" style={{ borderColor: THEME.border }}>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl p-3 text-center" style={{ background: '#fdf8ee' }}>
                  <p className="text-2xl font-black" style={{ color: THEME.primary }}>{d.totalRecordedDonations}</p>
                  <p className="text-xs" style={{ color: THEME.textMuted }}>Total Donations</p>
                </div>
                <div className="rounded-xl p-3 text-center" style={{ background: '#fdf8ee' }}>
                  <p className="text-2xl font-black" style={{ color: THEME.gold }}>{(d.totalRecordedDonations / (d.donorsWhoDonated || 1)).toFixed(1)}</p>
                  <p className="text-xs" style={{ color: THEME.textMuted }}>Avg per Donor</p>
                </div>
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* ── Donation Trends ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Monthly Donations (Last 6 Months)" icon="📈">
          <BarChart data={d.donationsByMonth} color="#16a34a" height={100} />
        </Section>
        
        {/* Top donors leaderboard */}
        <Section title="Top Donors Leaderboard" icon="🏆">
          {d.topDonors.length === 0 ? (
            <p className="text-xs text-center py-4" style={{ color: THEME.textMuted }}>No donation records yet</p>
          ) : (
            <div className="space-y-3">
              {d.topDonors.map((donor, i) => (
                <div key={donor.id} className="flex items-center gap-3">
                  <span className="text-base w-6 text-center shrink-0">{['🥇','🥈','🥉','4️⃣','5️⃣'][i]}</span>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-xs shrink-0"
                    style={{ background: `linear-gradient(135deg, ${THEME.primaryDark}, ${THEME.primary})` }}>
                    {donor.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: THEME.brown }}>{donor.name}</p>
                    <p className="text-xs truncate" style={{ color: THEME.textMuted }}>{donor.blood_group} · {donor.district}</p>
                  </div>
                  <span className="text-sm font-bold shrink-0" style={{ color: THEME.primary }}>
                    {donor.actualDonations} 💉
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Admin summary */}
        <Section title="Admin Summary" icon="⚙️">
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b" style={{ borderColor: THEME.border }}>
              <span className="text-sm" style={{ color: THEME.brown }}>Total Admins</span>
              <span className="font-bold text-lg" style={{ color: THEME.brown }}>{a.totalAdmins}</span>
            </div>
            {Object.entries(a.roleMap).map(([role, count]) => (
              <div key={role} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span>{role === 'SUPER_ADMIN' ? '👑' : role === 'DISTRICT_ADMIN' ? '📍' : '👤'}</span>
                  <span className="text-sm" style={{ color: THEME.brown }}>
                    {role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </div>
                <span className="font-bold" style={{ color: THEME.brown }}>{count}</span>
              </div>
            ))}
            <div className="mt-4 pt-3 border-t" style={{ borderColor: THEME.border }}>
              <div className="rounded-xl p-3 text-xs space-y-1.5" style={{ background: '#fdf8ee' }}>
                <div className="flex justify-between">
                  <span style={{ color: THEME.textMuted }}>Fulfillment rate</span>
                  <span className="font-bold" style={{ color: '#16a34a' }}>{r.fulfillmentRate}%</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: THEME.textMuted }}>Donor availability</span>
                  <span className="font-bold" style={{ color: '#3b82f6' }}>
                    {d.totalDonors > 0 ? Math.round(d.availableDonors / d.totalDonors * 100) : 0}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: THEME.textMuted }}>Eligible to donate now</span>
                  <span className="font-bold" style={{ color: THEME.primary }}>{d.eligibleNow}</span>
                </div>
              </div>
            </div>
          </div>
        </Section>
      </div>

    </div>
  );
};

export default AdminAnalytics;
